import { useRef, useState } from 'react';

const getCauseTitle = (cause) => cause?.title || cause?.name || cause?.issue || 'Diagnostic check';
const getCauseWhy = (cause) => cause?.whyItFits || cause?.why || cause?.reasoning || '';

const getToolLabel = (cause, step) => {
    if (step?.toolLabel) return step.toolLabel;
    if (step?.tools) {
        if (Array.isArray(step.tools)) return step.tools.filter(Boolean).join(', ');
        return step.tools;
    }
    if (cause?.toolSummary) return cause.toolSummary;
    if (Array.isArray(cause?.requiredTools) && cause.requiredTools.length > 0) {
        return cause.requiredTools.filter(Boolean).join(', ');
    }
    if (Array.isArray(cause?.tools) && cause.tools.length > 0) {
        return cause.tools.filter(Boolean).join(', ');
    }
    return '';
};

const CheckIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="m6.8 10.2 2.1 2.1 4.4-4.7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" />
    </svg>
);

const WarningIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <path d="M10 3.3 17 16H3L10 3.3Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.6" />
        <path d="M10 7.4v4.2M10 14.2h.01" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
    </svg>
);

const SkipIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <path d="M5 5.2v9.6l7-4.8-7-4.8Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7" />
        <path d="M15 5v10" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
);

const HelpIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <path d="M10 16.5a6.5 6.5 0 1 0-5.1-2.5l-1.1 2.8 3-.9c.9.4 1.9.6 3.2.6Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
    </svg>
);

const QuestionIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8.2 8.1a2 2 0 1 1 3.3 1.5c-.9.7-1.5 1.1-1.5 2.1M10 14.4h.01" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
);

const ToolIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4 shrink-0">
        <path d="m12.1 4.2 3.7 3.7-7.9 7.9H4.2v-3.7l7.9-7.9Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.55" />
        <path d="m10.8 5.5 3.7 3.7" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.55" />
    </svg>
);

const ChevronLeftIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
);

// Prev/next stepper used in the guided-check card header so the whole walkthrough
// stays in ONE card — arrows move between steps in place instead of stacking cards.
const StepNavButton = ({ onClick, disabled, label, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#d9d9d9] bg-white text-[#414141] transition hover:bg-[#F9F9F9] disabled:cursor-not-allowed disabled:opacity-40"
    >
        {children}
    </button>
);

const CardHeader = ({ label, title, stepText }) => (
    <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] px-3.5 py-2">
        <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">{label}</p>
            {stepText && (
                <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-black text-[#414141]">
                    {stepText}
                </span>
            )}
        </div>
        {title && <p className="mt-1.5 text-[13px] font-bold text-[#414141]">{title}</p>}
    </div>
);

const GuidedCardShell = ({ children, className = '' }) => (
    <div className={`w-full max-w-[460px] max-h-[calc(100vh-250px)] overflow-y-auto rounded-2xl border border-[#d9d9d9] bg-white shadow-none md:max-h-[560px] ${className}`}>
        {children}
    </div>
);

const getStepTotal = (totalSteps) => Math.max(Number(totalSteps) || 1, 1);

const ActionButton = ({ children, onClick, variant = 'neutral', icon, className = '', disabled = false }) => {
    const styles = {
        success: 'border-[#8ee6ac] bg-[#effdf4] text-[#096b34] hover:bg-[#e3faec]',
        warning: 'border-[#f4c76a] bg-[#fff8e8] text-[#a04a00] hover:bg-[#fff1cf]',
        neutral: 'border-[#d9d9d9] bg-white text-[#727272] hover:bg-[#F9F9F9]',
        primary: 'border-[#d9d9d9] bg-white text-[#414141] hover:border-[#D81F26] hover:text-[#D81F26]',
        dark: 'border-black bg-black text-white hover:bg-black/85',
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`flex min-h-[36px] items-center justify-center gap-2 rounded-xl border px-3 py-1.5 text-[13px] font-black transition ${styles[variant] || styles.neutral} ${disabled ? 'cursor-not-allowed opacity-40 hover:bg-white' : ''} ${className}`}
        >
            {icon}
            <span>{children}</span>
        </button>
    );
};

const CompactActionButton = ({ children, onClick, variant = 'dark' }) => {
    const variantClass = variant === 'light'
        ? 'border border-[#d9d9d9] bg-white text-[#515151] hover:bg-[#F9F9F9]'
        : variant === 'success'
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-black text-white hover:bg-black/85';
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.12em] transition ${variantClass}`}
        >
            {children}
        </button>
    );
};

const getExpectedResult = (cause, step) => (
    step?.expectedResult ||
    step?.expected ||
    cause?.expectedResult ||
    cause?.normalResult ||
    cause?.expected_result ||
    'The checked item looks normal, secure, and does not show abnormal movement, damage, corrosion, leaks, or noise.'
);

const getTestingFor = (cause, step) => (
    step?.testingFor ||
    step?.testTarget ||
    cause?.title ||
    cause?.recommendedCheck ||
    'this diagnostic check'
);

const ResultChoice = ({ value, selected, title, description, variant, icon, onClick }) => {
    const styles = {
        normal: selected
            ? 'bg-[#f0fff5] text-[#096b34]'
            : 'bg-transparent text-[#414141] hover:bg-[#F9F9F9]',
        issue: selected
            ? 'bg-[#fff8e8] text-[#a04a00]'
            : 'bg-transparent text-[#414141] hover:bg-[#F9F9F9]',
        not_sure: selected
            ? 'bg-[#fff9e8] text-[#8b5200]'
            : 'bg-transparent text-[#414141] hover:bg-[#F9F9F9]',
    };

    return (
        <button
            type="button"
            onClick={() => onClick(value)}
            className={`flex min-h-[38px] w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] font-black transition ${styles[variant] || styles.not_sure}`}
            title={description}
        >
            <span className="shrink-0">{icon}</span>
            <span className="min-w-0">
                <span className="block truncate">{title}</span>
                {selected && <span className="mt-0.5 block truncate text-xs font-semibold text-[#6d6d6d]">{description}</span>}
            </span>
        </button>
    );
};

export const GuidedCheckStepCard = ({ cause, step, stepIndex, totalSteps, onAction, onAskHelp, onPrev, onNext, canPrev = false, canNext = false }) => {
    const toolLabel = getToolLabel(cause, step);
    const total = getStepTotal(totalSteps);

    return (
        <GuidedCardShell>
            {/* Header: label left; step counter + prev/next steppers top-RIGHT. The whole
                check lives in ONE card — the arrows move between steps in place. */}
            <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] px-3.5 py-2">
                <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Diagnostic Check</p>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-black text-[#414141]">
                            Step {stepIndex + 1} of {total}
                        </span>
                        <StepNavButton onClick={onPrev} disabled={!canPrev} label="Previous step">
                            <ChevronLeftIcon />
                        </StepNavButton>
                        <StepNavButton onClick={onNext} disabled={!canNext} label="Next step">
                            <ChevronRightIcon />
                        </StepNavButton>
                    </div>
                </div>
                <p className="mt-1.5 text-[13px] font-bold text-[#414141]">{getCauseTitle(cause)}</p>
            </div>
            <div className="px-3.5 py-2.5">
                <h3 className="text-[15px] font-black leading-snug text-black">
                    {step?.instruction || cause?.recommendedCheck || 'Continue this diagnostic check.'}
                </h3>
                {step?.detail && (
                    <p className="mt-1.5 text-[13px] font-semibold leading-relaxed text-black/60">{step.detail}</p>
                )}
                {toolLabel && (
                    <div className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-[#414141]">
                        <ToolIcon />
                        <span>{toolLabel}</span>
                    </div>
                )}
            </div>
            <div className="border-t border-[#d9d9d9] px-3.5 py-2.5">
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">
                    What do you observe?
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <ActionButton onClick={() => onAction('normal')} variant="success" icon={<CheckIcon />}>
                        Looks Normal
                    </ActionButton>
                    <ActionButton onClick={() => onAction('issue')} variant="warning" icon={<WarningIcon />}>
                        Found an Issue
                    </ActionButton>
                    <ActionButton onClick={() => onAction('not_sure')} variant="neutral">
                        Not Sure
                    </ActionButton>
                    <ActionButton onClick={() => onAction('skip')} variant="neutral" icon={<SkipIcon />}>
                        Skip
                    </ActionButton>
                </div>
                <div className="mt-2">
                    <ActionButton onClick={onAskHelp} variant="primary" icon={<HelpIcon />} className="w-full">
                        Ask CarFido for Help
                    </ActionButton>
                </div>
            </div>
        </GuidedCardShell>
    );
};

export const GuidedCheckResultCard = ({ cause, step, selectedOutcome, notes, onSelectOutcome, onNotesChange, onSave, onAskHelp }) => (
    <GuidedCardShell>
        <CardHeader label="Check Complete" title={getCauseTitle(cause)} />
        <div className="border-b border-[#d9d9d9] px-3.5 py-2.5">
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Expected Result</p>
            <p className="mt-1 text-[13px] font-semibold leading-relaxed text-[#414141]">{getExpectedResult(cause, step)}</p>
        </div>
        <div className="px-3.5 py-2.5">
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">What did you find?</p>
            <p className="mt-1.5 text-[13px] font-semibold leading-snug text-[#414141]">
                Testing for: <span className="font-black text-black">{getTestingFor(cause, step)}</span>
            </p>
            <div className="mt-2.5 overflow-hidden rounded-xl border border-[#d9d9d9] bg-white">
                <ResultChoice
                    value="normal"
                    selected={selectedOutcome === 'normal'}
                    title="Normal"
                    description="This cause looks less likely based on the result."
                    variant="normal"
                    icon={<CheckIcon />}
                    onClick={onSelectOutcome}
                />
                <div className="h-px bg-[#d9d9d9]" />
                <ResultChoice
                    value="issue"
                    selected={selectedOutcome === 'issue'}
                    title="Issue Found"
                    description="This cause may be confirmed based on the result."
                    variant="issue"
                    icon={<WarningIcon />}
                    onClick={onSelectOutcome}
                />
                <div className="h-px bg-[#d9d9d9]" />
                <ResultChoice
                    value="not_sure"
                    selected={selectedOutcome === 'not_sure'}
                    title="Not Sure"
                    description="You couldn't get a clear reading or couldn't finish the check."
                    variant="not_sure"
                    icon={<QuestionIcon />}
                    onClick={onSelectOutcome}
                />
            </div>
            <p className="mt-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Notes & Readings (Optional)</p>
            <textarea
                value={notes || ''}
                onChange={(e) => onNotesChange(e.target.value)}
                className="mt-1.5 min-h-[52px] w-full rounded-xl border border-[#d9d9d9] p-2.5 text-[13px] font-semibold outline-none focus:border-[#D81F26]"
                placeholder="e.g. 12.1V at rest, engine cranked slowly, terminals looked clean..."
            />
            <p className="mt-2 text-xs font-semibold text-[#414141]">Measurements and notes will appear in the shop-ready report.</p>
            <div className="mt-2.5">
                <button
                    type="button"
                    onClick={onSave}
                    disabled={!selectedOutcome}
                    className="w-full rounded-xl bg-[#D81F26] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:bg-[#c7c7c7]"
                >
                    Save Result
                </button>
            </div>
            {onAskHelp && (
                <button
                    type="button"
                    onClick={onAskHelp}
                    className="mt-3 flex w-full items-center justify-center gap-2 text-[13px] font-black text-[#414141] hover:text-[#D81F26]"
                >
                    <HelpIcon />
                    Not sure about this result? Ask CarFido
                </button>
            )}
        </div>
    </GuidedCardShell>
);

export const GuidedCheckSavedResultCard = ({ cause, step, outcome, notes, onAskHelp }) => {
    const resultConfig = {
        normal: {
            label: 'Result was normal / as expected',
            body: 'This cause looks less likely based on the result.',
            shell: 'border-[#7edfa2] bg-[#f4fff7] text-[#075c2b]',
            icon: <CheckIcon />,
        },
        issue: {
            label: 'Found an issue / result was abnormal',
            body: 'This cause may be confirmed based on the result.',
            shell: 'border-[#ff8a8e] bg-[#fff6f6] text-[#a5131a]',
            icon: <WarningIcon />,
        },
        not_sure: {
            label: "Not sure / couldn't complete",
            body: "You couldn't get a clear reading or couldn't finish the check.",
            shell: 'border-[#f4c76a] bg-[#fff9e8] text-[#8b5200]',
            icon: <QuestionIcon />,
        },
    };
    const result = resultConfig[outcome] || resultConfig.not_sure;

    return (
        <GuidedCardShell>
            <CardHeader label={outcome === 'issue' ? 'Abnormal Result' : outcome === 'normal' ? 'Normal Result' : 'Unclear Result'} title={getCauseTitle(cause)} />
        <div className="border-b border-[#d9d9d9] px-3.5 py-2.5">
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Expected Result</p>
            <p className="mt-1 text-[13px] font-semibold leading-relaxed text-[#414141]">{getExpectedResult(cause, step)}</p>
        </div>
        <div className="px-3.5 py-3">
            <div className={`flex items-start gap-3 rounded-xl border p-3 ${result.shell}`}>
                    <span className="mt-0.5 shrink-0">{result.icon}</span>
                    <span>
                        <span className="block text-sm font-black leading-tight">{result.label}</span>
                        <span className="mt-1 block text-xs font-semibold leading-snug text-[#6d6d6d]">{notes || result.body}</span>
                    </span>
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-3 text-xs font-black">
                    <span className="text-[#09823f]">Saved to your symptom report</span>
                    {onAskHelp && (
                        <button type="button" onClick={onAskHelp} className="flex items-center gap-1 text-[#D81F26]">
                            <HelpIcon />
                            Ask CarFido
                        </button>
                    )}
                </div>
            </div>
        </GuidedCardShell>
    );
};

export const GuidedStepSelectionCard = ({ cause, step, action, label }) => {
    const actionConfig = {
        normal: {
            title: label || 'Looks Normal',
            body: 'This step looked normal and CarFido moved to the next check.',
            shell: 'border-[#8ee6ac] bg-[#f3fff7] text-[#075c2b]',
            icon: <CheckIcon />,
        },
        issue: {
            title: label || 'Found an Issue',
            body: 'You saw something abnormal on this step.',
            shell: 'border-[#f4c76a] bg-[#fff8e8] text-[#8b5200]',
            icon: <WarningIcon />,
        },
        not_sure: {
            title: label || 'Not Sure',
            body: "You couldn't confirm this step clearly.",
            shell: 'border-[#d9d9d9] bg-white text-[#414141]',
            icon: <QuestionIcon />,
        },
        skip: {
            title: label || 'Skipped',
            body: 'This step was skipped for now.',
            shell: 'border-[#d9d9d9] bg-white text-[#414141]',
            icon: <SkipIcon />,
        },
    };
    const selected = actionConfig[action] || actionConfig.not_sure;

    return (
        <div className={`w-full max-w-[360px] rounded-2xl border shadow-none animate-dash-selection-pop ${selected.shell}`}>
            <div className="px-3 py-2.5">
                <div className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0">{selected.icon}</span>
                    <span className="min-w-0">
                        <span className="block text-sm font-black leading-tight">{selected.title}</span>
                        <span className="mt-1 block truncate text-xs font-semibold text-[#6d6d6d]">
                            {step?.instruction || cause?.title || selected.body}
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
};

export const GuidedCardSkeleton = ({ label = 'Preparing next card' }) => (
    <GuidedCardShell>
        <div className="px-3.5 py-3">
            <div className="flex items-center justify-between gap-3">
                <div className="h-3 w-32 animate-pulse rounded-full bg-[#d9d9d9]" />
                <div className="h-6 w-20 animate-pulse rounded-full bg-[#F9F9F9]" />
            </div>
            <div className="mt-4 h-4 w-4/5 animate-pulse rounded-full bg-[#d9d9d9]" />
            <div className="mt-2.5 h-3.5 w-2/3 animate-pulse rounded-full bg-[#F9F9F9]" />
            <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="h-9 animate-pulse rounded-xl bg-[#edf5ef]" />
                <div className="h-9 animate-pulse rounded-xl bg-[#fff2d5]" />
            </div>
            <p className="mt-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">{label}</p>
        </div>
    </GuidedCardShell>
);

export const NextLikelyCauseCard = ({ cause, ruledOutTitle, onStart }) => (
    <GuidedCardShell>
        <CardHeader label="Next Check" title={ruledOutTitle ? `${ruledOutTitle} is less likely.` : 'Continue diagnosis'} />
        <div className="px-3.5 py-3">
            <h3 className="text-base font-black text-black">{getCauseTitle(cause)}</h3>
            <p className="mt-1.5 text-[13px] font-semibold leading-relaxed text-black/60">
                {getCauseWhy(cause) || 'Continue with the next most likely cause and run the guided steps.'}
            </p>
            <div className="mt-4">
                <CompactActionButton onClick={onStart}>Start Check</CompactActionButton>
            </div>
        </div>
    </GuidedCardShell>
);

export const ConfirmedCauseCard = ({ cause, onStartRepairGuide, onViewReport }) => (
    <GuidedCardShell>
        <CardHeader label="Cause Supported" title="Recommended next step is ready" />
        <div className="px-3.5 py-3">
            <h3 className="text-base font-black text-black">{getCauseTitle(cause)}</h3>
            {getCauseWhy(cause) && (
                <p className="mt-1.5 text-[13px] font-semibold leading-relaxed text-black/55">{getCauseWhy(cause)}</p>
            )}
            <p className="mt-1.5 text-[13px] font-semibold leading-relaxed text-black/60">
                {cause?.likelyRepair || cause?.recommendedCheck || 'Review the report for repair next steps.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
                <CompactActionButton onClick={onStartRepairGuide}>Repair Guide</CompactActionButton>
                <CompactActionButton onClick={onViewReport} variant="light">View Report</CompactActionButton>
            </div>
        </div>
    </GuidedCardShell>
);

// Generic pre-repair safety precautions. Five items — every one must be acknowledged
// before the repair guide opens. This is a single up-front screen, separate from any
// per-step hazard warning inside the guide.
export const REPAIR_SAFETY_ITEMS = [
    'Park on level ground with the engine off, the key removed, and the parking brake set.',
    'Let the engine, exhaust, and fluids fully cool before you touch them.',
    'Wear safety glasses and mechanic gloves.',
    'If you raise the vehicle, support it on jack stands — never work under a car held up by a jack alone.',
    "Disconnect the battery's negative terminal before doing any electrical work.",
];

// Shown before the FIRST repair step. The repair guide opens only after every item is
// acknowledged; tapping Continue with any item unchecked flags the missing ones and
// refuses to start. Acknowledgement is intentionally NOT persisted, so reopening a repair
// (or deep-linking straight to a step) presents this checklist again first.
export const RepairSafetyChecklistCard = ({ onContinue, items }) => {
    // Prefer the diagnosis-specific precautions; fall back to the generic set only when the
    // report didn't supply any (so an empty list can never leave the driver unable to proceed).
    const list = (Array.isArray(items) && items.length) ? items : REPAIR_SAFETY_ITEMS;
    const [checked, setChecked] = useState({});
    const [attempted, setAttempted] = useState(false);
    const allChecked = list.length > 0 && list.every((_, i) => checked[i]);

    const toggle = (i) => {
        setChecked((current) => ({ ...current, [i]: !current[i] }));
        setAttempted(false);
    };
    const handleContinue = () => {
        if (!allChecked) { setAttempted(true); return; }
        onContinue?.();
    };

    return (
        <GuidedCardShell>
            <CardHeader label="Before you start" title="Safety checklist" stepText="Confirm to begin" />
            <div className="px-3.5 py-3">
                <p className="text-[13px] font-semibold leading-relaxed text-black/60">
                    Confirm each safety step, then the repair guide will open at Step 1.
                </p>
                <ul className="mt-3 space-y-2">
                    {list.map((item, i) => {
                        const isChecked = Boolean(checked[i]);
                        const flag = attempted && !isChecked;
                        return (
                            <li key={i}>
                                <button
                                    type="button"
                                    onClick={() => toggle(i)}
                                    aria-pressed={isChecked}
                                    className={`flex w-full items-start gap-2.5 rounded-xl border px-3 py-2 text-left transition ${
                                        isChecked
                                            ? 'border-[#bde7d5] bg-[#f2fbf6]'
                                            : flag
                                            ? 'border-[#f4b0b0] bg-[#fff5f5]'
                                            : 'border-[#e7e7e7] bg-white hover:border-[#d9d9d9]'
                                    }`}
                                >
                                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                        isChecked
                                            ? 'border-[#0a8f53] bg-[#0a8f53] text-white'
                                            : flag
                                            ? 'border-[#d9534f] bg-white'
                                            : 'border-[#d9d9d9] bg-white'
                                    }`}>
                                        {isChecked && <CheckIcon />}
                                    </span>
                                    <span className={`text-[13px] font-semibold leading-relaxed ${flag ? 'text-[#b42318]' : 'text-black/75'}`}>
                                        {item}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {attempted && !allChecked && (
                    <p className="mt-2 text-[12px] font-bold text-[#b42318]">Please acknowledge every item to start the repair.</p>
                )}
                <div className="mt-3">
                    <ActionButton onClick={handleContinue} variant={allChecked ? 'success' : 'neutral'} icon={<CheckIcon />}>
                        Continue to repair
                    </ActionButton>
                </div>
            </div>
        </GuidedCardShell>
    );
};

export const RepairGuideStepCard = ({ step, stepIndex, totalSteps, onComplete, onSkip, onAskDash }) => (
    <GuidedCardShell>
        <CardHeader label="Repair Guide" title="Work through this step carefully" stepText={`Step ${stepIndex + 1} of ${getStepTotal(totalSteps)}`} />
        <div className="px-3.5 py-3">
            <h3 className="text-[15px] font-black leading-snug text-black">{step?.instruction}</h3>
            {step?.detail && <p className="mt-1.5 text-[13px] font-semibold leading-relaxed text-black/60">{step.detail}</p>}
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <ActionButton onClick={onComplete} variant="success" icon={<CheckIcon />}>Complete</ActionButton>
                <ActionButton onClick={onSkip} variant="neutral" icon={<SkipIcon />}>Skip</ActionButton>
                <ActionButton onClick={onAskDash} variant="primary" icon={<HelpIcon />}>Ask CarFido</ActionButton>
            </div>
        </div>
    </GuidedCardShell>
);

export const RepairGuideAllStepsCard = ({ cause, steps = [], onComplete, onAskDash }) => {
    const stepList = Array.isArray(steps) ? steps.filter(Boolean) : [];
    const repairTitle = cause?.likelyRepair || cause?.recommendedRepair || cause?.title || cause?.recommendedCheck || 'this repair';
    return (
        <GuidedCardShell>
            <CardHeader
                label="DIY Repair Guide"
                title={repairTitle}
                stepText={`${getStepTotal(stepList.length)} step${stepList.length === 1 ? '' : 's'}`}
            />
            <div className="px-3.5 py-3">
                <p className="text-[13px] font-semibold leading-relaxed text-black/55">
                    Follow every step below in order. Take your time and complete each one before moving to the next.
                </p>
                <ol className="mt-3 space-y-3">
                    {stepList.map((step, index) => (
                        <li key={index} className="flex gap-3">
                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] text-[13px] font-black text-[#414141]">
                                {index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-[15px] font-black leading-snug text-black">
                                    {step?.instruction || `Step ${index + 1}`}
                                </h3>
                                {step?.detail && (
                                    <p className="mt-1 text-[13px] font-semibold leading-relaxed text-black/60">{step.detail}</p>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <ActionButton onClick={onComplete} variant="success" icon={<CheckIcon />}>Mark repair complete</ActionButton>
                    <ActionButton onClick={onAskDash} variant="primary" icon={<HelpIcon />}>Ask CarFido</ActionButton>
                </div>
            </div>
        </GuidedCardShell>
    );
};

// ── Per-step hazard warnings ──────────────────────────────────────────────────
// Each repair step can carry one or more hazards. Warnings render ABOVE the step text,
// never below/after it. A `requiresAck` hazard (lifting) hides the step instructions and
// blocks Next until it's acknowledged. Hazards are read from explicit step metadata AND
// inferred from the step text, so a genuinely hazardous step is never shown with no warning
// even if its metadata is missing.
const HAZARD_DEFS = {
    hot: {
        label: 'Hot-component hazard',
        requiresAck: false,
        message: 'This step is near hot parts. Switch the engine off and let the engine and exhaust cool completely before you touch anything.',
        test: (t) => /\b(exhaust|manifold|catalytic|radiator|coolant|muffler|turbo|cylinder ?head|valve ?cover|spark ?plug|glow ?plug|engine bay|engine compartment|under the hood|hot (?:surface|component|part|exhaust)|(?:engine|exhaust)(?: is| runs)? hot|let (?:it|the engine|the exhaust)[\w\s]{0,24}cool|allow[\w\s]{0,24}cool(?:ed| down)?|(?:engine|it) (?:is|has|to) cool|cooled down|brake (?:pad|rotor|disc|caliper))\b/i.test(t),
    },
    pressurized: {
        label: 'Pressurised-system hazard',
        requiresAck: false,
        message: 'This system is under pressure. Relieve the pressure and wear eye protection before opening any line, port, or cap.',
        test: (t) => /\b(refrigerant|a\/?c\b|air[- ]?condition|pressuri[sz]|service port|schrader|fuel line|pressure line|charged system)\b/i.test(t),
    },
    electrical: {
        label: 'Electrical hazard — high voltage',
        requiresAck: false,
        message: 'High-voltage / electrical hazard. Disconnect power (and follow high-voltage safety on a hybrid or EV) before touching wiring, terminals, or the battery.',
        test: (t) => /\b(high[- ]?voltage|hv\b|hybrid|ev\b|electric shock|electrocut|traction battery|battery disconnect|disconnect the (?:negative )?battery|live wire|ignition coil|coil pack|ignition system|spark ?plug wire|alternator|starter motor)\b/i.test(t),
    },
    lifting: {
        label: 'Lifting hazard',
        requiresAck: true,
        message: 'Vehicle-lifting hazard. Raise the vehicle and support it on jack stands — never work under a car held up by a jack alone.',
        test: (t) => /\b(jack ?stand|jack\b|lift the|raise the|hoist|ramps?\b|underneath|under the (?:car|vehicle)|support the vehicle)\b/i.test(t),
    },
};

const normalizeHazardType = (value) => {
    const s = String(value || '').toLowerCase();
    if (/hot|heat|burn|exhaust|thermal/.test(s)) return 'hot';
    if (/press|refrig|pneumatic|a\/?c/.test(s)) return 'pressurized';
    if (/elect|volt|shock|\bhv\b|hybrid/.test(s)) return 'electrical';
    if (/lift|jack|raise|fall|crush|underneath/.test(s)) return 'lifting';
    return s;
};

export const getStepHazards = (step = {}) => {
    const found = new Map();
    const add = (type, custom) => {
        const def = HAZARD_DEFS[type];
        if (!def && !custom?.message) return;
        if (found.has(type)) return;
        found.set(type, {
            type,
            label: custom?.label || def?.label || 'Safety hazard',
            message: custom?.message || def?.message || 'This step can be hazardous — work carefully and use protective equipment.',
            requiresAck: custom?.requiresAck ?? def?.requiresAck ?? false,
        });
    };

    // 1) Explicit metadata (strings or objects).
    const explicit = step.hazards ?? step.warnings ?? step.hazardTypes ?? step.hazard ?? [];
    (Array.isArray(explicit) ? explicit : [explicit]).forEach((h) => {
        if (!h) return;
        if (typeof h === 'string') { add(normalizeHazardType(h)); return; }
        add(normalizeHazardType(h.type || h.kind || h.hazard || h.label), {
            label: h.label || h.title,
            message: h.message || h.text || h.warning,
            requiresAck: h.requiresAck ?? h.blocking,
        });
    });

    // 2) Infer from the step text so a step is never left un-warned when metadata is missing.
    const text = `${step.instruction || ''} ${step.detail || ''}`;
    Object.entries(HAZARD_DEFS).forEach(([type, def]) => { if (def.test(text)) add(type); });

    // 3) A step flagged hazardous but with nothing resolved → generic (blocking) warning.
    const flagged = step.hazardous === true || step.isHazardous === true || step.requiresWarning === true;
    if (found.size === 0 && flagged) {
        found.set('generic', {
            type: 'generic', label: 'Safety warning', requiresAck: true,
            message: 'This step can be hazardous. Work carefully and use appropriate protective equipment.',
        });
    }
    return Array.from(found.values());
};

const HazardWarnIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" />
    </svg>
);

const StepHazardWarnings = ({ hazards }) => (
    <div className="mb-3 space-y-2">
        {hazards.map((h) => (
            <div key={h.type} className="rounded-xl border border-[#f4b0b0] bg-[#fff5f5] px-3 py-2">
                <p className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.08em] text-[#b42318]">
                    <HazardWarnIcon /> {h.label}
                </p>
                <p className="mt-0.5 text-[12px] font-semibold leading-relaxed text-[#8a3a34]">{h.message}</p>
            </div>
        ))}
    </div>
);

// One-step-at-a-time DIY repair card: shows a single step with prev/next steppers
// (top-right) and a step counter, so the whole repair walkthrough lives in ONE card
// — matching the guided diagnostic-check card.
export const RepairGuideStepperCard = ({ cause, steps = [], stepIndex = 0, onPrev, onNext, onComplete, canPrev = false, canNext = false }) => {
    const stepList = Array.isArray(steps) ? steps.filter(Boolean) : [];
    const total = getStepTotal(stepList.length);
    const idx = Math.min(Math.max(Number(stepIndex) || 0, 0), Math.max(stepList.length - 1, 0));
    const step = stepList[idx] || {};
    const isLast = idx >= stepList.length - 1;
    const repairTitle = cause?.likelyRepair || cause?.recommendedRepair || cause?.title || cause?.recommendedCheck || 'this repair';

    // Hazards for THIS step. Warnings render as an informational banner above the step —
    // no acknowledgement gate: the step content is always visible and Next always works.
    const hazards = getStepHazards(step);

    return (
        <GuidedCardShell>
            <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] px-3.5 py-2">
                <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">DIY Repair Guide</p>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-black text-[#414141]">
                            Step {idx + 1} of {total}
                        </span>
                        <StepNavButton onClick={onPrev} disabled={!canPrev} label="Previous step">
                            <ChevronLeftIcon />
                        </StepNavButton>
                        <StepNavButton onClick={onNext} disabled={!canNext} label="Next step">
                            <ChevronRightIcon />
                        </StepNavButton>
                    </div>
                </div>
                <p className="mt-1.5 text-[13px] font-bold text-[#414141]">{repairTitle}</p>
            </div>
            <div className="px-3.5 py-3">
                {/* Hazard warnings appear before the step content, informational only. */}
                {hazards.length > 0 && <StepHazardWarnings hazards={hazards} />}
                <div className="flex gap-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] text-[13px] font-black text-[#414141]">
                        {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                        <h3 className="text-[15px] font-black leading-snug text-black">
                            {step?.instruction || `Step ${idx + 1}`}
                        </h3>
                        {step?.detail && (
                            <p className="mt-1 text-[13px] font-semibold leading-relaxed text-black/60">{step.detail}</p>
                        )}
                    </div>
                </div>
            </div>
            <div className="border-t border-[#d9d9d9] px-3.5 py-2.5">
                <div className="grid grid-cols-2 gap-2">
                    <ActionButton onClick={onPrev} disabled={!canPrev} variant="neutral" icon={<ChevronLeftIcon />}>Previous step</ActionButton>
                    {isLast ? (
                        <ActionButton onClick={onComplete} variant="success" icon={<CheckIcon />}>Mark repair complete</ActionButton>
                    ) : (
                        <ActionButton onClick={onNext} disabled={!canNext} variant="primary" icon={<ChevronRightIcon />}>Next step</ActionButton>
                    )}
                </div>
            </div>
        </GuidedCardShell>
    );
};

export const RepairOutcomeCard = ({ onFixed, onNotFixed, onUnsure }) => (
    <GuidedCardShell>
        <CardHeader label="Monitoring" title="Did this fix the issue?" />
        <div className="px-3.5 py-3">
            <h3 className="text-base font-black text-black">Is the original problem gone?</h3>
            <p className="mt-1 text-[13px] font-semibold text-[#414141]">
                The ticket is now being monitored. It’s only marked resolved once you confirm the issue is gone — if it’s still there, CarFido keeps diagnosing on the same ticket.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
                <CompactActionButton onClick={onFixed} variant="success">Yes, it’s fixed</CompactActionButton>
                {onUnsure && <CompactActionButton onClick={onUnsure} variant="light">Not sure yet</CompactActionButton>}
                <CompactActionButton onClick={onNotFixed} variant="light">No, still happening</CompactActionButton>
            </div>
        </div>
    </GuidedCardShell>
);

export const RepairLogChoiceCard = ({ onLogNow, onLater }) => (
    <GuidedCardShell>
        <CardHeader label="Repair Complete" title="Save this to vehicle history" />
        <div className="px-3.5 py-3">
            <h3 className="text-base font-black text-black">Log this repair?</h3>
            <div className="mt-3 flex flex-wrap gap-2">
                <CompactActionButton onClick={onLogNow}>Log Now</CompactActionButton>
                <CompactActionButton onClick={onLater} variant="light">Later</CompactActionButton>
            </div>
        </div>
    </GuidedCardShell>
);

export const RepairLogFormCard = ({ mileage, note, onMileageChange, onNoteChange, onBack, onSubmit, isSubmitting, error, locked = false, distanceUnit = 'mi' }) => (
    <GuidedCardShell>
        <CardHeader label="Repair Log" title={locked ? 'Logged — saved to this ticket' : 'Add final repair details'} />
        <div className="px-3.5 py-3">
            <h3 className="text-base font-black text-black">{locked ? `Odometer reading (${distanceUnit === 'km' ? 'km' : 'mi'})` : 'Repair details'}</h3>
            <input
                value={mileage || ''}
                onChange={(e) => { if (!locked) onMileageChange(e.target.value); }}
                readOnly={locked}
                inputMode="numeric"
                className={`mt-2.5 w-full rounded-xl border border-[#d9d9d9] p-2.5 text-[13px] font-semibold outline-none focus:border-[#D81F26] ${locked ? 'cursor-not-allowed bg-[#f4f4f4] text-black/55' : ''}`}
                placeholder={`Odometer reading (${distanceUnit === 'km' ? 'km' : 'mi'})`}
            />
            <textarea
                value={note || ''}
                onChange={(e) => { if (!locked) onNoteChange(e.target.value); }}
                readOnly={locked}
                className={`mt-2.5 min-h-[68px] w-full rounded-xl border border-[#d9d9d9] p-2.5 text-[13px] font-semibold outline-none focus:border-[#D81F26] ${locked ? 'cursor-not-allowed bg-[#f4f4f4] text-black/55' : ''}`}
                placeholder="Notes"
            />
            {!locked && error && <p className="mt-2 text-sm font-bold text-red-600">{error}</p>}
            {!locked && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <CompactActionButton onClick={onSubmit}>{isSubmitting ? 'Saving...' : 'Submit'}</CompactActionButton>
                    <CompactActionButton onClick={onBack} variant="light">Back</CompactActionButton>
                </div>
            )}
            {locked && (
                <p className="mt-3 text-[11px] font-semibold text-black/45">
                    Repair log submitted and saved to this ticket's history. It can no longer be edited.
                </p>
            )}
        </div>
    </GuidedCardShell>
);

export const RepairLoggedCard = ({ cause }) => (
    <GuidedCardShell>
        <CardHeader label="Repair Logged" title="Vehicle history updated" />
        <div className="px-3.5 py-3">
            <h3 className="text-base font-black text-black">Repair logged</h3>
            <p className="mt-1.5 text-[13px] font-semibold text-black/60">
                {cause?.likelyRepair || getCauseTitle(cause)} is marked complete.
            </p>
        </div>
    </GuidedCardShell>
);

const CameraIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <path d="M3 6.5h2.4l1-1.6h5.2l1 1.6H17v9H3v-9Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5" />
        <circle cx="10" cy="10.8" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
);

const VideoIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
        <rect x="3" y="6" width="9.5" height="8" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="m12.5 9 4-2v6l-4-2V9Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5" />
    </svg>
);

const CloseIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-3.5 w-3.5">
        <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
    </svg>
);

const EVIDENCE_MAX_FILES = 10;
const EVIDENCE_MAX_BYTES = 25 * 1024 * 1024; // 25MB — videos run larger than photos

const formatBytes = (size) => {
    if (!size && size !== 0) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Shown when the user reports "Found an Issue" during a guided DIY check. Lets them
// attach photos/videos and/or type what they saw; on save the parent stores it on the
// ticket, classified as Evidence. Skippable — evidence is encouraged, not required.
export const EvidenceCaptureCard = ({ checkTitle, onSubmit, onSkip, isSubmitting = false, error = '', saved = null }) => {
    const [files, setFiles] = useState([]);
    const [note, setNote] = useState('');
    const [localError, setLocalError] = useState('');
    const fileInputRef = useRef(null);

    if (saved) {
        return (
            <GuidedCardShell>
                <CardHeader label="Evidence Saved" title={checkTitle || 'Diagnostic check'} />
                <div className="px-3.5 py-3">
                    <div className="flex items-start gap-3 rounded-xl border border-[#8ee6ac] bg-[#f3fff7] p-3 text-[#075c2b]">
                        <span className="mt-0.5 shrink-0"><CheckIcon /></span>
                        <span>
                            <span className="block text-sm font-black leading-tight">Saved to this ticket as evidence</span>
                            <span className="mt-1 block text-xs font-semibold leading-snug text-[#6d6d6d]">
                                {saved.mediaCount > 0 && `${saved.mediaCount} file${saved.mediaCount === 1 ? '' : 's'}`}
                                {saved.mediaCount > 0 && saved.noteSaved ? ' and ' : ''}
                                {saved.noteSaved && 'your note'}
                                {(saved.mediaCount > 0 || saved.noteSaved) ? ' were attached to the symptom report.' : 'Evidence recorded.'}
                            </span>
                        </span>
                    </div>
                </div>
            </GuidedCardShell>
        );
    }

    const addFiles = (fileList) => {
        setLocalError('');
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        if (files.length + incoming.length > EVIDENCE_MAX_FILES) {
            setLocalError(`You can add up to ${EVIDENCE_MAX_FILES} files.`);
            return;
        }
        incoming.forEach((file) => {
            if (file.size > EVIDENCE_MAX_BYTES) {
                setLocalError(`${file.name} is larger than 25MB.`);
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const isImage = (file.type || '').startsWith('image/');
                setFiles((prev) => [...prev, {
                    id: `${file.name}-${file.size}-${prev.length}`,
                    filename: file.name,
                    mime_type: file.type || 'application/octet-stream',
                    size: file.size,
                    base64: String(reader.result).split(',')[1],
                    previewSrc: isImage ? reader.result : '',
                }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

    const canSubmit = !isSubmitting && (files.length > 0 || note.trim().length > 0);

    const handleSubmit = () => {
        if (!canSubmit) return;
        onSubmit?.({
            note: note.trim(),
            files: files.map(({ previewSrc, ...rest }) => rest),
        });
    };

    return (
        <GuidedCardShell>
            <CardHeader label="Found an Issue" title={checkTitle || 'Add evidence for this check'} />
            <div className="px-3.5 py-3">
                <h3 className="text-[15px] font-black leading-snug text-black">Add evidence of what you found</h3>
                <p className="mt-1 text-[13px] font-semibold leading-relaxed text-black/55">
                    Attach photos or a short video, and/or describe what you saw. It’s saved to this
                    ticket as evidence and appears in your shop-ready report.
                </p>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = null; }}
                />

                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-3 py-3 text-[13px] font-black text-[#414141] transition hover:bg-[#F2F2F2] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <CameraIcon />
                    <span>Add photos or video</span>
                </button>

                {files.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {files.map((file) => (
                            <div key={file.id} className="relative overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#F9F9F9]">
                                <button
                                    type="button"
                                    onClick={() => removeFile(file.id)}
                                    aria-label={`Remove ${file.filename}`}
                                    className="absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
                                >
                                    <CloseIcon />
                                </button>
                                {file.previewSrc ? (
                                    <img src={file.previewSrc} alt={file.filename} className="h-16 w-full object-cover" />
                                ) : (
                                    <div className="flex h-16 w-full flex-col items-center justify-center gap-1 text-[#414141]">
                                        <VideoIcon />
                                        <span className="max-w-full truncate px-1 text-[10px] font-bold">{formatBytes(file.size)}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <p className="mt-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">What did you see? (Optional)</p>
                <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={isSubmitting}
                    className="mt-1.5 min-h-[60px] w-full rounded-xl border border-[#d9d9d9] p-2.5 text-[13px] font-semibold outline-none focus:border-[#D81F26] disabled:bg-[#f4f4f4]"
                    placeholder="e.g. Cracked hose near the top of the radiator, coolant residue around the clamp..."
                />

                {(localError || error) && (
                    <p className="mt-2 text-sm font-bold text-red-600">{localError || error}</p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="rounded-full bg-black px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white transition hover:bg-black/85 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {isSubmitting ? 'Saving...' : 'Save evidence'}
                    </button>
                    <button
                        type="button"
                        onClick={onSkip}
                        disabled={isSubmitting}
                        className="rounded-full border border-[#d9d9d9] bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-[#515151] transition hover:bg-[#F9F9F9] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Skip
                    </button>
                </div>
            </div>
        </GuidedCardShell>
    );
};
